import { supabase } from './supabase';

export type AssistantMessage = {
  id: string;
  role: 'user' | 'assistant';
  kind: 'text' | 'audio' | 'transcript' | 'action_result' | 'error' | 'confirmation';
  content: string;
  created_at: string;
  audio_duration_ms?: number | null;
};

export type AssistantResult = {
  ok: boolean;
  message: string;
  transcript?: string;
  conversation_id?: string;
  replayed?: boolean;
  pending?: boolean;
};

export function createIdempotencyKey() {
  const nativeCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (nativeCrypto?.randomUUID) return nativeCrypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function errorMessage(error: any) {
  if (typeof error?.context?.json === 'function') return error.context.json().then((body: any) => body?.message || body?.error || 'Não foi possível concluir.');
  return Promise.resolve(error?.message || 'Não foi possível concluir.');
}

export async function loadAssistantHistory(): Promise<AssistantMessage[]> {
  const { data: conversation, error: conversationError } = await supabase
    .from('assistant_conversations').select('id').order('last_message_at', { ascending: false }).limit(1).maybeSingle();
  if (conversationError || !conversation?.id) return [];
  const { data, error } = await supabase.from('assistant_messages')
    .select('id,role,kind,content,created_at,audio_duration_ms')
    .eq('conversation_id', conversation.id).order('created_at', { ascending: true }).limit(80);
  if (error) throw new Error(error.message);
  return (data ?? []) as AssistantMessage[];
}

export async function sendAssistantText(content: string, idempotencyKey = createIdempotencyKey()): Promise<AssistantResult> {
  const { data, error } = await supabase.functions.invoke('finance-assistant', {
    body: { content, idempotency_key: idempotencyKey },
  });
  if (error) throw new Error(await errorMessage(error));
  if (!data?.ok) throw new Error(data?.message || 'Não foi possível concluir.');
  return data as AssistantResult;
}

export async function sendAssistantAudio(blob: Blob, durationMs: number, idempotencyKey = createIdempotencyKey()): Promise<AssistantResult> {
  const form = new FormData();
  // Safari can hand us a Blob whose filename is empty (or whose MIME type is
  // only present on the recorder). Promote it to a File so multipart parsing
  // in the Edge Function receives a stable name and content type.
  const mimeType = blob.type || 'audio/mp4';
  const extension = mimeType.includes('mp4') || mimeType.includes('m4a') ? 'm4a'
    : mimeType.includes('mpeg') || mimeType.includes('mp3') ? 'mp3'
      : mimeType.includes('ogg') ? 'ogg' : 'webm';
  const filename = `gravacao.${extension}`;
  const audioFile = typeof File !== 'undefined' ? new File([blob], filename, { type: mimeType }) : blob;
  form.append('audio', audioFile, filename);
  form.append('duration_ms', String(Math.round(durationMs)));
  form.append('idempotency_key', idempotencyKey);
  const { data, error } = await supabase.functions.invoke('finance-assistant', { body: form });
  if (error) throw new Error(await errorMessage(error));
  if (!data?.ok) throw new Error(data?.message || 'Não foi possível concluir.');
  return data as AssistantResult;
}
